import React, { useState, useContext } from 'react';
import { AuthContext } from '@/App';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Mail, MessageSquare, HelpCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    category: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const categories = [
    'Supporto Tecnico',
    'Domanda sul Profilo',
    'Problema con Annuncio',
    'Questioni di Pagamento',
    'Suggerimento/Feedback',
    'Richiesta Nuova Categoria',
    'Segnalazione Bug',
    'Altro'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.category || !formData.message) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSending(true);
    
    // Simulazione invio (in produzione, qui ci sarebbe una chiamata API)
    setTimeout(() => {
      toast.success('Messaggio inviato con successo! Ti risponderemo entro 24-48 ore.');
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        subject: '',
        category: '',
        message: ''
      });
      setSending(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-6 py-8 mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-playfair font-bold text-[#344B71] mb-4" data-testid="contact-title">
              Contattaci
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Hai domande, suggerimenti o hai bisogno di assistenza? Siamo qui per aiutarti!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="p-6 bg-white">
                <div className="w-12 h-12 bg-[#E8A007] rounded-xl flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#344B71] mb-2">Email</h3>
                <p className="text-slate-600 text-sm mb-3">
                  Per supporto generale e richieste
                </p>
                <a href="mailto:supporto@consultanthub.it" className="text-sm text-[#344B71] hover:text-[#344B71]">
                  supporto@consultanthub.it
                </a>
              </Card>

              <Card className="p-6 bg-white">
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center mb-4">
                  <HelpCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#344B71] mb-2">FAQ</h3>
                <p className="text-slate-600 text-sm mb-3">
                  Trova risposte immediate alle domande più comuni
                </p>
                <button 
                  onClick={() => window.location.href = '/faq'}
                  className="text-sm text-[#344B71] hover:text-[#344B71]"
                >
                  Vai alle FAQ →
                </button>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-[#344B71] to-[#344B71] text-white">
                <h3 className="text-lg font-semibold mb-2">Tempo di Risposta</h3>
                <p className="text-sm text-slate-300">
                  Rispondiamo a tutte le richieste entro <strong className="text-white">24-48 ore</strong> lavorative.
                </p>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="p-8 bg-white">
                <h2 className="text-2xl font-playfair font-bold text-[#344B71] mb-6">
                  Invia un Messaggio
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                        Nome Completo *
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Mario Rossi"
                        required
                        className="mt-1 h-12"
                        data-testid="name-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="mario.rossi@example.com"
                        required
                        className="mt-1 h-12"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="category" className="text-sm font-medium text-slate-700">
                      Categoria *
                    </Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => setFormData({...formData, category: value})}
                    >
                      <SelectTrigger className="mt-1 h-12" data-testid="category-select">
                        <SelectValue placeholder="Seleziona categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-sm font-medium text-slate-700">
                      Oggetto *
                    </Label>
                    <Input
                      id="subject"
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder="Breve descrizione del problema o richiesta"
                      required
                      className="mt-1 h-12"
                      data-testid="subject-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-slate-700">
                      Messaggio *
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={8}
                      placeholder="Descrivi in dettaglio la tua richiesta o il problema che stai riscontrando..."
                      required
                      className="mt-1"
                      data-testid="message-input"
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button
                      type="submit"
                      disabled={sending}
                      className="bg-[#344B71] hover:bg-[#344B71] text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all"
                      data-testid="submit-button"
                    >
                      {sending ? (
                        'Invio in corso...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Invia Messaggio
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;
